import { useMemo, useState } from "react";
import { productMatches, searchWords } from "@/lib/search";
import { Input } from "@/components/ui/input";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DemandUpload } from "@/components/DemandUpload";
import { DemandRow } from "@/components/DemandRow";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search } from "lucide-react";

const ALL = "tutte";

const Totals = ({ t }) => (
  <div className="mb-4 flex flex-wrap gap-6 text-sm" data-testid="demand-totals">
    <span><span className="font-display text-2xl font-semibold">{num(t.restock)}</span> richieste "avvisami" in attesa</span>
    <span><span className="font-display text-2xl font-semibold">{num(t.wishlist)}</span> capi in wishlist</span>
    {t.unmatched > 0 && <span className="text-amber-400" data-testid="demand-unmatched">{num(t.unmatched)} righe non abbinate a un prodotto</span>}
  </div>
);

export default function Richieste() {
  const [season, setSeason] = useState(ALL);
  const { data, isLoading } = useQuery({
    queryKey: ["demand", season],
    queryFn: async () => (await api.get("/demand", { params: season === ALL ? {} : { season } })).data,
    placeholderData: keepPreviousData,
  });
  const [search, setSearch] = useState("");
  const items = useMemo(() => (data?.items || []).filter((r) => productMatches(r, searchWords(search))), [data, search]);
  return (
    <div data-testid="richieste-page">
      <PageHeader title="Richieste clienti" subtitle="Avvisi di ritorno disponibilità e wishlist, confrontati con magazzino e materie prime: cosa produrre." />
      <div className="mb-6 grid gap-4 md:grid-cols-2">
        {Object.entries(data?.sources || { restock: "SW Back in Stock", wishlist: "Advanced Wishlist" }).map(([k, l]) => (
          <DemandUpload key={k} source={k} label={l} info={data?.imports?.[k]} />
        ))}
      </div>
      <Card className="p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display text-lg font-semibold">Capi più richiesti</h3>
          <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nome o codice (es. 8001 a26 nero)" className="h-8 w-64 pl-8 text-xs" data-testid="demand-search" />
          </div>
          <Select value={season} onValueChange={setSeason}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="demand-season"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tutte le stagioni</SelectItem>
              {(data?.seasons || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          </div>
        </div>
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
          <>
            <Totals t={data.totals} />
            <p className="mb-2 text-xs text-muted-foreground">Ordinati per richieste "avvisami" (contano doppio) e wishlist. Clicca un capo per il dettaglio per taglia: in rosso le taglie richieste e senza giacenza.</p>
            <div className="hidden grid-cols-[2fr_100px_100px_100px_100px_170px_20px] gap-2 border-b border-border/60 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground md:grid">
              <span>Capo</span><span className="text-right">Avvisami</span><span className="text-right">Wishlist</span><span className="text-right">Magazzino</span><span className="text-right">Producibili</span><span>Materiali</span><span />
            </div>
            {items.map((r) => <DemandRow key={r.id} r={r} />)}
            {!data.items.length && <p className="py-6 text-center text-sm text-muted-foreground" data-testid="demand-empty">Nessuna richiesta: carica i file esportati dalle due app</p>}
            {data.items.length > 0 && !items.length && <p className="py-6 text-center text-sm text-muted-foreground" data-testid="demand-search-empty">Nessun capo corrisponde alla ricerca.</p>}
          </>
        )}
      </Card>
    </div>
  );
}
